import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCheck, Gift, Inbox, Loader2, Paperclip, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  claimInboxReward,
  createInboxTicket,
  deleteInboxMessage,
  downloadInboxAttachment,
  listInbox,
  listInboxTickets,
  markAllInboxRead,
  markInboxRead,
  type InboxMessage,
  type InboxTicketCategory,
} from "@/lib/boide-api";
import { useBoideUser } from "@/hooks/use-boide-user";

export const Route = createFileRoute("/_authenticated/hop-thu")({
  head: () => ({
    meta: [
      { title: "Hộp Thư — Thông Báo & Quà Tặng" },
      {
        name: "description",
        content: "Hộp thư Bói Đê: nhận thông báo, quà thưởng và gửi góp ý / khiếu nại / đề xuất.",
      },
    ],
  }),
  component: HopThuPage,
});

const vnd = (n: number) => `${n.toLocaleString("vi-VN")}đ`;
const fmtDate = (s: string) => new Date(s.replace(" ", "T")).toLocaleString("vi-VN");

// Bỏ cú pháp markdown để hiển thị preview 1 dòng gọn gàng (không lộ **, #, []()).
const stripMarkdown = (md: string): string =>
  md
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // ảnh / link -> giữ text
    .replace(/[*_`~#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// Components map render markdown cho nội dung thư (bám tông gold của app).
const INBOX_MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-2 first:mt-0 last:mb-0 leading-relaxed">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-primary">{children}</strong>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline underline-offset-2 [overflow-wrap:anywhere] hover:opacity-80"
    >
      {children}
    </a>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-relaxed">{children}</li>
  ),
};

function HopThuPage() {
  return (
    <main className="app-feature-page flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-3 py-6 pb-24 md:py-10">
        <header className="mb-6 text-center">
          <span className="app-inbox-hero-icon" aria-hidden="true">
            <Inbox className="h-7 w-7 text-primary" />
          </span>
          <h1 className="font-serif-soft text-2xl text-foreground md:text-3xl">Hộp thư</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Nhận thông báo, quà thưởng và gửi góp ý tới Bói Đê.
          </p>
        </header>

        <Tabs defaultValue="messages">
          <TabsList className="mb-4 grid w-full grid-cols-2">
            <TabsTrigger value="messages">Thư đến</TabsTrigger>
            <TabsTrigger value="tickets">Gửi Bói Đê</TabsTrigger>
          </TabsList>
          <TabsContent value="messages">
            <MessagesTab />
          </TabsContent>
          <TabsContent value="tickets">
            <TicketsTab />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function MessagesTab() {
  const qc = useQueryClient();
  const { refresh } = useBoideUser();
  const { confirm, confirmDialog } = useConfirm();
  const query = useQuery({ queryKey: ["inbox"], queryFn: () => listInbox({ limit: 50 }) });
  const messages = query.data?.items ?? [];
  const [selected, setSelected] = useState<InboxMessage | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["inbox"] });
    qc.invalidateQueries({ queryKey: ["inbox-unread"] });
  };

  const allReadMut = useMutation({
    mutationFn: () => markAllInboxRead(),
    onSuccess: () => {
      invalidate();
      toast.success("Đã đánh dấu tất cả đã đọc");
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteInboxMessage(id),
    onSuccess: () => {
      invalidate();
      setSelected(null);
      toast.success("Đã xoá thư");
    },
  });
  const claimMut = useMutation({
    mutationFn: (id: number) => claimInboxReward(id),
    onSuccess: (res) => {
      invalidate();
      refresh();
      setSelected((s) => (s ? { ...s, claimed: true } : s));
      toast.success(`Nhận thưởng thành công +${vnd(res.reward_amount)}`);
    },
    onError: (e: Error) => toast.error(e.message || "Không nhận được thưởng"),
  });

  const openMessage = (m: InboxMessage) => {
    setSelected(m);
    if (!m.is_read) {
      markInboxRead(m.id)
        .then(invalidate)
        .catch(() => {});
    }
  };

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Inbox className="mx-auto mb-3 h-10 w-10 text-primary/70" />
          Hộp thư trống.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => allReadMut.mutate()}
          disabled={allReadMut.isPending}
          className="gap-2"
        >
          <CheckCheck className="h-4 w-4" /> Đọc tất cả
        </Button>
      </div>

      {/* Danh sách kiểu Gmail: 1 dòng, bấm để mở chi tiết */}
      {messages.map((m) => (
        <button
          key={m.id}
          onClick={() => openMessage(m)}
          className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition hover:border-primary/60 ${
            m.is_read
              ? "border-gold-soft/30 bg-background/40"
              : "border-primary/50 bg-background/60"
          }`}
        >
          {!m.is_read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`truncate ${m.is_read ? "text-foreground/80" : "font-semibold text-foreground"}`}
              >
                {m.title}
              </span>
              {m.has_reward && !m.claimed && !m.reward_expired && (
                <Badge className="shrink-0 gap-1 bg-gold-gradient text-primary-foreground">
                  <Gift className="h-3 w-3" /> Quà
                </Badge>
              )}
              {m.attachments && m.attachments.length > 0 && (
                <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
            </div>
            <p className="truncate text-xs text-muted-foreground">{stripMarkdown(m.body_md)}</p>
          </div>
          <span className="shrink-0 text-[11px] uppercase tracking-widest text-muted-foreground">
            {fmtDate(m.created_at)}
          </span>
        </button>
      ))}

      <MessageDialog
        message={selected}
        onClose={() => setSelected(null)}
        onDelete={async (id) => {
          // Thư có quà CHƯA NHẬN mà xoá đi là mất luôn phần thưởng — nói rõ ra,
          // đừng để người dùng biết sau khi đã bấm.
          const unclaimed = Boolean(selected?.reward_amount && !selected?.claimed);
          const ok = await confirm({
            title: "Xoá thư này?",
            description: unclaimed ? (
              <>
                Thư này còn phần thưởng <b>chưa nhận</b> — xoá đi là mất luôn. Hành động này không
                thể hoàn tác.
              </>
            ) : (
              <>Thư sẽ bị xoá khỏi hộp thư của bạn. Hành động này không thể hoàn tác.</>
            ),
            confirmLabel: "Xoá thư",
          });
          if (ok) deleteMut.mutate(id);
        }}
        onClaim={(id) => claimMut.mutate(id)}
        claiming={claimMut.isPending}
      />
      {confirmDialog}
    </div>
  );
}

function MessageDialog({
  message,
  onClose,
  onDelete,
  onClaim,
  claiming,
}: {
  message: InboxMessage | null;
  onClose: () => void;
  onDelete: (id: number) => void | Promise<void>;
  onClaim: (id: number) => void;
  claiming: boolean;
}) {
  if (!message) return null;
  const m = message;
  return (
    <Dialog open={!!message} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg gap-3 overflow-y-auto overflow-x-hidden rounded-2xl border-gold-soft/50 p-5 sm:rounded-2xl sm:p-6">
        <DialogHeader className="min-w-0 space-y-1.5 text-left">
          <DialogTitle className="pr-8 font-display text-lg text-foreground [overflow-wrap:anywhere]">
            {m.title}
          </DialogTitle>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {fmtDate(m.created_at)}
          </p>
        </DialogHeader>
        <div className="border-t border-gold-soft/25" />
        <div className="min-w-0 text-sm leading-relaxed text-foreground/90 [overflow-wrap:anywhere]">
          <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS} components={INBOX_MD_COMPONENTS}>
            {m.body_md}
          </ReactMarkdown>
        </div>

        {m.attachments && m.attachments.length > 0 && (
          <div className="min-w-0 space-y-2 rounded-lg border border-gold-soft/30 bg-background/40 p-3">
            <p className="text-xs font-semibold text-muted-foreground">📎 Tệp đính kèm</p>
            {m.attachments.map((a, i) => (
              <button
                key={i}
                onClick={() =>
                  downloadInboxAttachment(m.id, i, a.name).catch(() =>
                    toast.error("Không tải được tệp"),
                  )
                }
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-primary hover:bg-card/60"
              >
                <Paperclip className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{a.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {Math.round((a.size || 0) / 1024)} KB
                </span>
              </button>
            ))}
          </div>
        )}

        {m.has_reward && (
          <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-gold-soft/40 bg-background/40 p-3">
            <Gift className="h-5 w-5 shrink-0 text-primary" />
            <span className="font-display text-primary">{vnd(m.reward_amount)}</span>
            {m.claimed ? (
              <Badge variant="outline">Đã nhận</Badge>
            ) : m.reward_expired ? (
              <Badge variant="outline" className="text-destructive">
                Đã hết hạn
              </Badge>
            ) : (
              <Button
                size="sm"
                className="ml-auto shrink-0 gap-2"
                onClick={() => onClaim(m.id)}
                disabled={claiming}
              >
                {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
                Nhận thưởng
              </Button>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground hover:text-destructive"
            onClick={() => void onDelete(m.id)}
          >
            <Trash2 className="h-4 w-4" /> Xoá thư
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORY_OPTIONS: { value: InboxTicketCategory; label: string }[] = [
  { value: "gop_y", label: "Góp ý" },
  { value: "khieu_nai", label: "Khiếu nại" },
  { value: "de_xuat", label: "Đề xuất" },
  { value: "khac", label: "Khác" },
];

function TicketsTab() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["inbox-tickets"], queryFn: () => listInboxTickets() });
  const tickets = query.data?.items ?? [];

  const [category, setCategory] = useState<InboxTicketCategory>("gop_y");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const createMut = useMutation({
    mutationFn: () => createInboxTicket({ category, subject, content, files }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["inbox-tickets"] });
      setSubject("");
      setContent("");
      setFiles([]);
      toast.success(res.message);
    },
    onError: (e: Error) => toast.error(e.message || "Gửi thất bại"),
  });

  const statusLabel: Record<string, string> = {
    open: "Đang chờ",
    replied: "Đã trả lời",
    closed: "Đã đóng",
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Gửi góp ý / khiếu nại / đề xuất</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as InboxTicketCategory)}
            className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Tiêu đề"
            className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Nội dung..."
            rows={4}
            className="w-full rounded-lg border border-gold-soft/40 bg-background/40 px-3 py-2 text-sm"
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Paperclip className="h-4 w-4" />
            <span>{files.length > 0 ? `${files.length} tệp đã chọn` : "Đính kèm tệp"}</span>
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </label>
          <Button
            className="gap-2"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !subject.trim() || !content.trim()}
          >
            {createMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Gửi
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {query.isLoading && <Skeleton className="h-20 w-full rounded-xl" />}
        {!query.isLoading && tickets.length === 0 && (
          <p className="text-center text-sm text-muted-foreground">Bạn chưa gửi yêu cầu nào.</p>
        )}
        {tickets.map((t) => (
          <Card key={t.id} className="border-gold-soft/40">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-sm">{t.subject}</CardTitle>
                <Badge variant="outline">{statusLabel[t.status] || t.status}</Badge>
              </div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {fmtDate(t.created_at)}
              </p>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm text-foreground/80">{t.content}</p>
              {t.status === "replied" && (
                <p className="mt-2 text-xs text-emerald-500">
                  Đã có phản hồi — xem ở tab "Thư đến".
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
